import { AbstractControl } from '@angular/forms';

export interface AdjustmentItem {
  control: AbstractControl;
  description: string;
  currentValue: number;
  index: number;
  newValue?: number;
  adjustment?: number;
}

export interface PercentageAdjustmentResult {
  changedItems: AdjustmentItem[];
  unchangedItems: AdjustmentItem[];
  difference: number;
  adjustmentPerItem: number;
}

export class PercentageAdjustmentUtils {
  
 
  static calculatePercentageAdjustments(controls: AbstractControl[]): PercentageAdjustmentResult | null {
    const activeCauses = this.filterActiveCauses(controls);
    const totalPercentage = this.calculateTotalPercentage(activeCauses);

    if (totalPercentage === 100) {
      return null;
    }

    const { changedItems, unchangedItems } = this.categorizeItems(activeCauses);

    if (unchangedItems.length === 0) {
      return this.handleOnlyChangedItems(changedItems, totalPercentage);
    }

    return this.handleNewLogicAdjustment(changedItems, unchangedItems, totalPercentage);
  }

  private static filterActiveCauses(controls: AbstractControl[]): AbstractControl[] {
    return controls.filter(control => !control.get('isDeleted')?.value);
  }

  private static calculateTotalPercentage(controls: AbstractControl[]): number {
    return controls.reduce((sum, control) => {
      const percentage = control.get('percentage')?.value || 0;
      return sum + percentage;
    }, 0);
  }

  private static categorizeItems(controls: AbstractControl[]): {
    changedItems: AdjustmentItem[];
    unchangedItems: AdjustmentItem[];
  } {
    const changedItems: AdjustmentItem[] = [];
    const unchangedItems: AdjustmentItem[] = [];

    controls.forEach((control, index) => {
      const currentValue = control.get('percentage')?.value || 0;
      const descriptionControl = control.get('description');
      const description = descriptionControl?.value || `Item ${index + 1}`;
      
      const adjustmentItem: AdjustmentItem = {
        control,
        description,
        currentValue,
        index
      };

      const isNew = control.get('isNew')?.value === true;
      
      const originalMap = control.get('originalMap')?.value || {};
      const originalPercentage = originalMap.percentage;
      
      const hasActualValueChange = originalPercentage !== undefined && 
                                   Math.abs(originalPercentage - currentValue) > 0.001; 

      if (isNew || hasActualValueChange) {
        changedItems.push(adjustmentItem);
      } else {
        unchangedItems.push(adjustmentItem);
      }
    });

    return { changedItems, unchangedItems };
  }

  private static handleOnlyChangedItems(
    changedItems: AdjustmentItem[], 
    totalPercentage: number
  ): PercentageAdjustmentResult {
    if (totalPercentage <= 100) {
      const difference = 100 - totalPercentage;
      const equalAdjustment = this.roundToTwoDecimals(difference / changedItems.length);
      
      const adjustedItems = changedItems.map(item => ({
        ...item,
        newValue: this.roundToTwoDecimals(item.currentValue + equalAdjustment),
        adjustment: equalAdjustment
      }));

      this.ensureExactTotal(adjustedItems);

      return {
        changedItems: adjustedItems,
        unchangedItems: [],
        difference,
        adjustmentPerItem: equalAdjustment
      };
    } else {
      return this.proportionallyReduceChangedItems(changedItems, totalPercentage);
    }
  }

  private static handleMixedItems(
    changedItems: AdjustmentItem[],
    unchangedItems: AdjustmentItem[],
    difference: number
  ): PercentageAdjustmentResult {
    const adjustmentPerItem = this.roundToTwoDecimals(difference / unchangedItems.length);

    return {
      changedItems,
      unchangedItems,
      difference,
      adjustmentPerItem
    };
  }

  private static handleNewLogicAdjustment(
    changedItems: AdjustmentItem[],
    unchangedItems: AdjustmentItem[],
    totalPercentage: number
  ): PercentageAdjustmentResult {
    const excess = totalPercentage - 100;

    const equalReductionPerItem = excess / unchangedItems.length;

    const canAllContributeEqually = unchangedItems.every(item => {
      const maxPossibleReduction = Math.max(0, item.currentValue - 1);
      return maxPossibleReduction >= equalReductionPerItem;
    });
    
    if (canAllContributeEqually) {
      return this.handleEqualDistribution(changedItems, unchangedItems, excess, equalReductionPerItem);
    } 

    return this.handleAscendingOrderDistribution(changedItems, unchangedItems, excess);
    
  }

  private static handleEqualDistribution(
    changedItems: AdjustmentItem[],
    unchangedItems: AdjustmentItem[],
    excess: number,
    equalReductionPerItem: number
  ): PercentageAdjustmentResult {
    const adjustedUnchangedItems = unchangedItems.map(item => ({
      ...item,
      newValue: this.roundToTwoDecimals(item.currentValue - equalReductionPerItem),
      adjustment: -equalReductionPerItem
    }));

    const adjustedChangedItems = changedItems.map(item => ({
      ...item,
      newValue: item.currentValue,
      adjustment: 0
    }));

    this.ensureExactTotalForMixed(adjustedChangedItems, adjustedUnchangedItems);

    return {
      changedItems: adjustedChangedItems,
      unchangedItems: adjustedUnchangedItems,
      difference: -excess,
      adjustmentPerItem: -equalReductionPerItem
    };
  }

  private static handleAscendingOrderDistribution(
    changedItems: AdjustmentItem[],
    unchangedItems: AdjustmentItem[],
    excess: number
  ): PercentageAdjustmentResult {
    const sortedUnchangedItems = [...unchangedItems].sort((a, b) => a.currentValue - b.currentValue);
    
    let remainingExcess = excess;
    const adjustedUnchangedItems: AdjustmentItem[] = [];

    let processedCount = 0;
    for (const item of sortedUnchangedItems) {
      const maxPossibleReduction = Math.max(0, item.currentValue - 1); // Can't go below 1%
      const actualReduction = Math.min(remainingExcess, maxPossibleReduction);
      
      const newValue = this.roundToTwoDecimals(item.currentValue - actualReduction);
      
      adjustedUnchangedItems.push({
        ...item,
        newValue: Math.max(1, newValue), // Ensure minimum 1%
        adjustment: Math.max(1, newValue) - item.currentValue
      });
      
      remainingExcess -= actualReduction;
      processedCount++;
      
      if (remainingExcess <= 0.01) {
        break;
      }
    }
    
    for (let i = processedCount; i < sortedUnchangedItems.length; i++) {
      const item = sortedUnchangedItems[i];
      adjustedUnchangedItems.push({
        ...item,
        newValue: item.currentValue,
        adjustment: 0
      });
    }
    
    if (remainingExcess > 0.01) { 
      return this.handleRemainingExcessFromChangedItems(adjustedUnchangedItems, changedItems, remainingExcess);
    } else {
      const adjustedChangedItems = changedItems.map(item => ({
        ...item,
        newValue: item.currentValue,
        adjustment: 0
      }));
      
      this.ensureExactTotalForMixed(adjustedChangedItems, adjustedUnchangedItems);
      
      return {
        changedItems: adjustedChangedItems,
        unchangedItems: adjustedUnchangedItems,
        difference: -excess,
        adjustmentPerItem: 0
      };
    }
  }

  private static handleRemainingExcessFromChangedItems(
    adjustedUnchangedItems: AdjustmentItem[],
    changedItems: AdjustmentItem[],
    remainingExcess: number
  ): PercentageAdjustmentResult {
    const changedTotal = changedItems.reduce((sum, item) => sum + item.currentValue, 0);
    
    if (changedTotal <= 0) {
      const adjustedChangedItems = changedItems.map(item => ({
        ...item,
        newValue: item.currentValue,
        adjustment: 0
      }));
      
      return {
        changedItems: adjustedChangedItems,
        unchangedItems: adjustedUnchangedItems,
        difference: remainingExcess, 
        adjustmentPerItem: 0
      };
    }
    
    const adjustedChangedItems = changedItems.map(item => {
      const proportionalReduction = (item.currentValue / changedTotal) * remainingExcess;
      const newValue = this.roundToTwoDecimals(item.currentValue - proportionalReduction);
      
      return {
        ...item,
        newValue: Math.max(0.01, newValue), 
        adjustment: Math.max(0.01, newValue) - item.currentValue
      };
    });
    
    this.ensureExactTotalForMixed(adjustedChangedItems, adjustedUnchangedItems);
    
    return {
      changedItems: adjustedChangedItems,
      unchangedItems: adjustedUnchangedItems,
      difference: -remainingExcess,
      adjustmentPerItem: 0
    };
  }


  private static proportionallyReduceChangedItems(
    changedItems: AdjustmentItem[],
    totalPercentage: number
  ): PercentageAdjustmentResult {
    const reductionFactor = 100 / totalPercentage;
    
    const adjustedItems = changedItems.map(item => ({
      ...item,
      newValue: this.roundToTwoDecimals(item.currentValue * reductionFactor),
      adjustment: this.roundToTwoDecimals((item.currentValue * reductionFactor) - item.currentValue)
    }));

    this.ensureExactTotal(adjustedItems);

    return {
      changedItems: adjustedItems,
      unchangedItems: [],
      difference: 100 - totalPercentage,
      adjustmentPerItem: 0 
    };
  }

  private static ensureExactTotal(adjustedItems: AdjustmentItem[]): void {
    const totalAdjusted = adjustedItems.reduce((sum, item) => sum + (item.newValue || 0), 0);
    
    if (totalAdjusted !== 100 && adjustedItems.length > 0) {
      const lastItem = adjustedItems[adjustedItems.length - 1];
      const correction = 100 - totalAdjusted;
      lastItem.newValue = this.roundToTwoDecimals((lastItem.newValue || 0) + correction);
      lastItem.adjustment = lastItem.newValue - lastItem.currentValue;
    }
  }

  private static ensureExactTotalForMixed(
    adjustedChangedItems: AdjustmentItem[],
    adjustedUnchangedItems: AdjustmentItem[]
  ): void {
    const totalChanged = adjustedChangedItems.reduce((sum, item) => sum + (item.newValue || 0), 0);
    const totalUnchanged = adjustedUnchangedItems.reduce((sum, item) => sum + (item.newValue || 0), 0);
    const total = totalChanged + totalUnchanged;
    
    if (total !== 100 && adjustedChangedItems.length > 0) {
      const correction = 100 - total;
      const lastChangedItem = adjustedChangedItems[adjustedChangedItems.length - 1];
      lastChangedItem.newValue = this.roundToTwoDecimals((lastChangedItem.newValue || 0) + correction);
      lastChangedItem.adjustment = lastChangedItem.newValue - lastChangedItem.currentValue;
    }
  }

  private static roundToTwoDecimals(value: number): number {
    return Math.round(value * 100) / 100;
  }

  static applyAdjustments(adjustmentResult: PercentageAdjustmentResult): void {
    const { changedItems, unchangedItems } = adjustmentResult;
    
    unchangedItems.forEach((item: AdjustmentItem) => {
      if (item.newValue !== undefined) {
        item.control.get('percentage')?.setValue(item.newValue);
      }
    });

    changedItems.forEach((item: AdjustmentItem) => {
      if (item.newValue !== undefined) {
        item.control.get('percentage')?.setValue(item.newValue);
      }
    });
  }

  static validateTotalPercentage(controls: AbstractControl[]): boolean {
    const activeCauses = this.filterActiveCauses(controls);
    const totalPercentage = this.calculateTotalPercentage(activeCauses);
    return totalPercentage === 100;
  }

  static getNewValue(currentValue: number, adjustment: number): number {
    return this.roundToTwoDecimals(currentValue + adjustment);
  }
}